import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Layer, LayerType } from '../types/layer';
import './LegendEditor.css';

// 범례 아이템 인터페이스
interface LegendItem {
  id: string;
  label: string;
  color: string;
  value?: string | number;
  symbol?: string;
  opacity?: number;
  description?: string;
}

// 범례 스타일 인터페이스
interface LegendStyle {
  layerId: string;
  layerName: string;
  items: LegendItem[];
  opacity: number;
  blendMode?: string;
}

interface LegendEditorProps {
  layer: Layer;
  onClose: () => void;
  onSave: (style: LegendStyle) => void;
}

const isItemTransparent = (item: LegendItem): boolean => (item.opacity ?? 1) <= 0;

const LegendEditor: React.FC<LegendEditorProps> = ({
  layer,
  onClose,
  onSave
}) => {
  const { t } = useTranslation();
  const [legendStyle, setLegendStyle] = useState<LegendStyle>({
    layerId: layer.id,
    layerName: layer.name,
    items: [],
    opacity: layer.opacity
  });

  const [newItem, setNewItem] = useState<Partial<LegendItem>>({
    label: '',
    color: '#ff0000',
    value: '',
    opacity: 1
  });

  // 기본 범례 아이템 생성 - 저장된 내용이 있으면 불러오기
  useEffect(() => {
    // 저장된 범례 스타일이 있는지 확인
    if (layer.legendStyle) {
      console.log('저장된 범례 스타일 불러오기:', layer.legendStyle);
      setLegendStyle({
        ...layer.legendStyle,
        opacity: layer.legendStyle.opacity || layer.opacity
      });
    } else {
      // 기본 범례 아이템 생성
    if (layer.type === LayerType.EGIS) {
      // EGIS 토지피복 기본 범례 (다국어 지원)
      const defaultItems: LegendItem[] = [
        { id: '1', label: t('legend.urban'), color: '#ff0000', value: '1' },
        { id: '2', label: t('legend.agricultural'), color: '#ffff00', value: '2' },
        { id: '3', label: t('legend.forest'), color: '#00ff00', value: '3' },
        { id: '4', label: t('legend.grassland'), color: '#90ee90', value: '4' },
        { id: '5', label: t('legend.wetland'), color: '#00ffff', value: '5' },
        { id: '6', label: t('legend.water'), color: '#0000ff', value: '6' },
        { id: '7', label: t('legend.other'), color: '#808080', value: '7' }
      ];
      setLegendStyle(prev => ({ ...prev, items: defaultItems }));
    } else {
        // 백엔드 API를 통한 범례 데이터 로드 (캐시 무효화)
        const fetchLegendFromAPI = async () => {
          try {
            console.log('범례 API 호출 시작:', layer.id);
            const apiUrl = `/api/upload/layers/local/${layer.id}/legend?_t=${Date.now()}`;
            console.log('API URL:', apiUrl);
            
            const response = await fetch(apiUrl);
            console.log('API 응답 상태:', response.status, response.statusText);
            
            if (response.ok) {
              const result = await response.json();
                          if (result.success && result.data) {
              const apiLegendData = result.data;
              console.log('API 응답 데이터:', apiLegendData);
              
              // API 응답을 LegendItem[] 형태로 변환
              const items: LegendItem[] = apiLegendData.items.map((item: any) => ({
                id: item.id,
                label: item.label,
                color: item.color,
                value: item.value || item.label,
                opacity: item.opacity ?? 1.0
              }));
              
              console.log('변환된 범례 아이템:', items);
              setLegendStyle(prev => ({ 
                ...prev, 
                items,
                opacity: apiLegendData.opacity || layer.opacity
              }));
              return;
            }
            }
            // API 호출 실패 시 기본값 사용
            const defaultItems: LegendItem[] = [
              { id: '1', label: '범주 1', color: '#ff0000', value: '1' },
              { id: '2', label: '범주 2', color: '#00ff00', value: '2' },
              { id: '3', label: '범주 3', color: '#0000ff', value: '3' }
            ];
            setLegendStyle(prev => ({ ...prev, items: defaultItems }));
          } catch (error) {
            console.error('범례 API 호출 실패:', error);
            console.error('에러 상세:', error);
            // 에러 발생 시 기본값 사용
      const defaultItems: LegendItem[] = [
        { id: '1', label: '범주 1', color: '#ff0000', value: '1' },
        { id: '2', label: '범주 2', color: '#00ff00', value: '2' },
        { id: '3', label: '범주 3', color: '#0000ff', value: '3' }
      ];
      setLegendStyle(prev => ({ ...prev, items: defaultItems }));
          }
        };

        // 비동기 함수 호출 (useEffect 내에서)
        fetchLegendFromAPI();
      }
    }
  }, [layer, layer.legendStyle, t]); // legendStyle 변경을 감지하도록 의존성 추가

  // 새 범례 아이템 추가
  const handleAddItem = () => {
    if (newItem.label && newItem.color) {
      const item: LegendItem = {
        id: Date.now().toString(),
        label: newItem.label,
        color: newItem.color,
        value: newItem.value || newItem.label,
        opacity: newItem.opacity ?? 1
      };
      
      setLegendStyle(prev => ({
        ...prev,
        items: [...prev.items, item]
      }));
      
      setNewItem({ label: '', color: '#ff0000', value: '', opacity: 1 });
    }
  };

  // 범례 아이템 삭제
  const handleRemoveItem = (id: string) => {
    setLegendStyle(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
  };

  // 범례 아이템 수정
  const handleUpdateItem = (id: string, field: keyof LegendItem, value: string) => {
    setLegendStyle(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === id ? { ...item, [field]: value } : item
      )
    }));
  };

  // 클래스별 투명 여부 토글
  const handleToggleItemTransparent = (id: string, transparent: boolean) => {
    setLegendStyle(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === id
          ? { ...item, opacity: transparent ? 0 : 1 }
          : item
      )
    }));
  };

  // 범례 스타일 저장 (백엔드 API 연동)
  const handleSave = async () => {
    console.log('범례 편집기에서 저장 요청:', legendStyle);
    
    try {
      // 백엔드 API로 범례 데이터 저장
      // layerId에서 'local-' 접두사 제거
      const actualLayerId = layer.id.replace('local-', '');
      const apiLegendData = {
        layerId: actualLayerId,
        version: "1.0",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        legendType: "categorized",
        items: legendStyle.items.map(item => ({
          id: item.id,
          label: item.label,
          color: item.color,
          opacity: item.opacity ?? 1.0,
          transparent: isItemTransparent(item),
          value: item.value || item.label,
          description: item.description
        })),
        metadata: {
          title: layer.name,
          description: `${layer.name} 범례`,
          units: ""
        }
      };
      
      console.log('저장할 범례 데이터:', apiLegendData);
      const saveUrl = `/api/upload/layers/local/${layer.id}/legend`;
      console.log('저장 API URL:', saveUrl);
      
      const response = await fetch(saveUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(apiLegendData)
      });
      
      console.log('저장 응답 상태:', response.status, response.statusText);
      
      if (response.ok) {
        const result = await response.json();
        console.log('범례 저장 성공:', result);
        
        // 로컬 상태도 업데이트
        onSave(legendStyle);
        onClose();
      } else {
        const errorText = await response.text();
        console.error('범례 저장 실패:', response.status, response.statusText);
        console.error('에러 응답:', errorText);
        // 실패해도 로컬 상태는 업데이트 (fallback)
        onSave(legendStyle);
        onClose();
      }
    } catch (error) {
      console.error('범례 저장 중 오류:', error);
      // 오류 발생 시에도 로컬 상태는 업데이트 (fallback)
    onSave(legendStyle);
    onClose();
    }
  };

  // QML 스타일 파일 생성
  const generateQMLStyle = () => {
    const qmlContent = `<!DOCTYPE qgis>
<qgis version="3.28.0-Firenze" styleCategories="Symbology|Labeling" labelsEnabled="0">
  <renderer-v2 type="categorizedSymbol" forceraster="0" attr="value" enableorderby="0">
    <categories>
${legendStyle.items.map(item => `      <category symbol="0" value="${item.value}" label="${item.label}"/>`).join('\n')}
    </categories>
    <symbols>
${legendStyle.items.map((item, index) => `      <symbol type="fill" name="${index}" alpha="${isItemTransparent(item) ? 0 : (item.opacity ?? 1)}" clip_to_extent="1" force_rhr="0">
        <layer class="SimpleFill" locked="0" pass="0" enabled="1">
          <prop k="border_width_map_unit_scale" v="3x:0,0,0,0,0,0"/>
          <prop k="color" v="${item.color}"/>
          <prop k="joinstyle" v="bevel"/>
          <prop k="offset" v="0,0"/>
          <prop k="offset_map_unit_scale" v="3x:0,0,0,0,0,0"/>
          <prop k="offset_unit" v="MM"/>
          <prop k="outline_color" v="35,35,35,255"/>
          <prop k="outline_style" v="solid"/>
          <prop k="outline_width" v="0.26"/>
          <prop k="outline_width_unit" v="MM"/>
          <prop k="style" v="solid"/>
        </layer>
      </symbol>`).join('\n')}
    </symbols>
    <source-symbol>
      <symbol type="fill" name="0" alpha="1" clip_to_extent="1" force_rhr="0">
        <layer class="SimpleFill" locked="0" pass="0" enabled="1">
          <prop k="border_width_map_unit_scale" v="3x:0,0,0,0,0,0"/>
          <prop k="color" v="243,166,178,255"/>
          <prop k="joinstyle" v="bevel"/>
          <prop k="offset" v="0,0"/>
          <prop k="offset_map_unit_scale" v="3x:0,0,0,0,0,0"/>
          <prop k="offset_unit" v="MM"/>
          <prop k="outline_color" v="35,35,35,255"/>
          <prop k="outline_style" v="solid"/>
          <prop k="outline_width" v="0.26"/>
          <prop k="outline_width_unit" v="MM"/>
          <prop k="style" v="solid"/>
        </layer>
      </symbol>
    </source-symbol>
    <colorramp type="randomcolors" name="[source]"/>
    <rotation/>
    <sizescale/>
  </renderer-v2>
  <blendMode>0</blendMode>
  <featureBlendMode>0</featureBlendMode>
  <layerOpacity>${legendStyle.opacity}</layerOpacity>
  <layerGeometryType>2</layerGeometryType>
</qgis>`;

    // QML 파일 다운로드
    const blob = new Blob([qmlContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${layer.name}_style.qml`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // 모달은 항상 표시 (부모에서 제어)

  return (
    <div className="legend-editor-overlay">
      <div className="legend-editor-modal">
        <div className="legend-editor-header">
          <h3>{t('legend.editLegend')} - {layer.name}</h3>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <div className="legend-editor-content">

          {/* 투명도 설정 */}
          <div className="opacity-section">
            <label>{t('layers.opacity')}: {Math.round(legendStyle.opacity * 100)}%</label>
            <input
              type="range"
              min="0"
              max="100"
              value={Math.round(legendStyle.opacity * 100)}
              onChange={(e) => setLegendStyle(prev => ({ 
                ...prev, 
                opacity: parseInt(e.target.value) / 100 
              }))}
            />
          </div>

          {/* 범례 아이템 목록 */}
          <div className="legend-items-section">
            <h4>{t('legend.userLegend')}</h4>
            <div className="legend-items-list">
              {legendStyle.items.map((item) => (
                <div key={item.id} className="legend-item">
                  <div className="item-color">
                    <input
                      type="color"
                      value={item.color}
                      disabled={isItemTransparent(item)}
                      onChange={(e) => handleUpdateItem(item.id, 'color', e.target.value)}
                    />
                  </div>
                  <label className="item-transparent-toggle" title={t('legend.transparent') as string}>
                    <input
                      type="checkbox"
                      checked={isItemTransparent(item)}
                      onChange={(e) => handleToggleItemTransparent(item.id, e.target.checked)}
                    />
                    <span>{t('legend.transparent')}</span>
                  </label>
                  <input
                    type="text"
                    value={item.label}
                    onChange={(e) => handleUpdateItem(item.id, 'label', e.target.value)}
                    className="item-label"
                    placeholder={t('legend.classificationName') as string}
                  />
                  <input
                    type="text"
                    value={item.value || ''}
                    onChange={(e) => handleUpdateItem(item.id, 'value', e.target.value)}
                    className="item-value"
                    placeholder={t('legend.classificationValue') as string}
                  />
                  <button
                    className="remove-item-btn"
                    onClick={() => handleRemoveItem(item.id)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>

            {/* 새 범례 아이템 추가 */}
            <div className="add-item-section">
              <h4>{t('legend.addClassification')}</h4>
              <div className="add-item-form">
                <input
                  type="color"
                  value={newItem.color}
                  disabled={(newItem.opacity ?? 1) <= 0}
                  onChange={(e) => setNewItem(prev => ({ ...prev, color: e.target.value }))}
                  className="new-item-color"
                />
                <label className="item-transparent-toggle" title={t('legend.transparent') as string}>
                  <input
                    type="checkbox"
                    checked={(newItem.opacity ?? 1) <= 0}
                    onChange={(e) => setNewItem(prev => ({
                      ...prev,
                      opacity: e.target.checked ? 0 : 1
                    }))}
                  />
                  <span>{t('legend.transparent')}</span>
                </label>
                <input
                  type="text"
                  value={newItem.label}
                  onChange={(e) => setNewItem(prev => ({ ...prev, label: e.target.value }))}
                  placeholder={t('legend.classificationName') as string}
                  className="new-item-label"
                />
                <input
                  type="text"
                  value={newItem.value || ''}
                  onChange={(e) => setNewItem(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={t('legend.classificationValue') as string}
                  className="new-item-value"
                />
                <button onClick={handleAddItem} className="add-item-btn">
                  {t('common.add')}
                </button>
              </div>
            </div>
          </div>

          {/* 범례 미리보기 */}
          <div className="legend-preview-section">
            <h4>{t('legend.title')} {t('common.preview')}</h4>
            <div className="legend-preview">
              {legendStyle.items.map((item) => (
                <div key={item.id} className="preview-item">
                  <div
                    className={`preview-color ${isItemTransparent(item) ? 'preview-color-transparent' : ''}`}
                    style={isItemTransparent(item) ? undefined : { backgroundColor: item.color }}
                  ></div>
                  <span className="preview-label">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="legend-editor-footer">
          <button onClick={generateQMLStyle} className="export-qml-btn">
            QML {t('common.export')}
          </button>
          <div className="action-buttons">
            <button onClick={onClose} className="cancel-btn">
              {t('common.cancel')}
            </button>
            <button onClick={handleSave} className="save-btn">
              {t('common.save')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegendEditor; 